
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import ConceptConfirmation from './ConceptConfirmation';
import { Button } from "@/components/ui/button";
import { Lightbulb, CheckCircle2, FileText, Scale, Loader2, Pen } from 'lucide-react';
import { motion } from "framer-motion";
import EmailTemplatePreviewButton from '../email-preview/EmailTemplatePreviewButton';
import { useToast } from '@/hooks/use-toast';
import TemplateVersionBanner from './TemplateVersionBanner';
import { useTemplateVersioning } from '@/hooks/useTemplateVersioning';

interface ChatMessageProps {
  message: {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    isEmail?: boolean;
    metadata?: any;
    templateId?: string;
    hasTemplate?: boolean;
  };
  onConceptConfirmed?: (concept: string) => void;
  requiresConfirmation?: boolean;
  onConfirmChanges?: (messageId: string, content: string) => void;
  extractedTemplate?: string | null;
  onEmailGenerated?: (content: string) => void;
  onOpenOfferForm?: () => void;
  onOpenComparisonForm?: () => void;
  onRequestIdeas?: (context: string) => void;
  triggerTemplateGeneration?: (concept: string) => Promise<void>;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onConceptConfirmed,
  requiresConfirmation,
  onConfirmChanges,
  extractedTemplate,
  onEmailGenerated,
  onOpenOfferForm,
  onOpenComparisonForm,
  onRequestIdeas,
  triggerTemplateGeneration,
}) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showPreviewButton, setShowPreviewButton] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [generationAttempts, setGenerationAttempts] = useState(0);
  const { toast } = useToast();
  
  // Add template versioning hook here
  const templateVersioning = useTemplateVersioning(message.templateId);
  
  const isWelcomeMessage = message.metadata?.isWelcomeMessage || 
                         message.metadata?.initialMessage;
  
  const isUser = message.role === 'user';
  
  console.log("Rendering message:", message.id, message.role, 
    "isUser:", isUser, 
    "metadata:", message.metadata, 
    "content:", message.content ? message.content.substring(0, 30) : "No content");

  // Monitor template generation events
  useEffect(() => {
    const handleGenerationStarted = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.concept === message.content) {
        console.log("[DEBUG] ChatMessage: Template generation started for this message");
        setIsConfirming(true);
        setGenerationError(null);
      }
    };
    
    const handleGenerationComplete = (e: Event) => {
      const customEvent = e as CustomEvent;
      console.log("[DEBUG] ChatMessage: Template generation complete event received");
      
      // Only update if this message triggered the generation
      if (isConfirming) {
        setIsConfirming(false);
        setIsConfirmed(true);
        setShowPreviewButton(true);
        
        if (customEvent.detail?.content && onEmailGenerated) {
          onEmailGenerated(customEvent.detail.content);
        }
      }
    };
    
    const handleGenerationFailed = (e: Event) => {
      const customEvent = e as CustomEvent;
      console.error("[ERROR] ChatMessage: Template generation failed:", customEvent.detail?.error);
      
      // Only update if this message triggered the generation
      if (isConfirming) {
        setIsConfirming(false);
        setGenerationError(customEvent.detail?.error || "Template-Generierung fehlgeschlagen");
        
        toast({
          title: "Fehler",
          description: "Die Template-Generierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.",
          variant: "destructive"
        });
      }
    };
    
    window.addEventListener('template-generation-started', handleGenerationStarted as EventListener);
    window.addEventListener('template-generation-complete', handleGenerationComplete as EventListener);
    window.addEventListener('template-generation-failed', handleGenerationFailed as EventListener);
    
    return () => {
      window.removeEventListener('template-generation-started', handleGenerationStarted as EventListener);
      window.removeEventListener('template-generation-complete', handleGenerationComplete as EventListener);
      window.removeEventListener('template-generation-failed', handleGenerationFailed as EventListener);
    };
  }, [isConfirming, toast, onEmailGenerated, message.content]);

  const handleConfirmConcept = async () => {
    if (!triggerTemplateGeneration || isConfirming || isConfirmed) {
      console.log("[DEBUG] Cannot generate template:", {
        noTriggerFunction: !triggerTemplateGeneration,
        isConfirming,
        isConfirmed
      });
      
      if (!triggerTemplateGeneration) {
        toast({
          title: "Fehler",
          description: "Template-Generierung ist derzeit nicht verfügbar",
          variant: "destructive"
        });
      }
      
      return;
    }
    
    console.log("[DEBUG] Starting template generation for concept:", message.content.substring(0, 50));
    setIsConfirming(true);
    setGenerationError(null);
    setGenerationAttempts(prev => prev + 1);

    try {
      // Dispatch event to notify that template generation is starting
      window.dispatchEvent(new CustomEvent('template-generation-started', {
        detail: { concept: message.content }
      }));
      
      if (onConceptConfirmed) {
        onConceptConfirmed(message.content);
      } else if (triggerTemplateGeneration) {
        await triggerTemplateGeneration(message.content);
      }
    } catch (error) {
      console.error("[ERROR] Template generation failed:", error);
      setIsConfirming(false);
      setGenerationError("Fehler bei der Template-Generierung");
      
      toast({
        title: "Fehler",
        description: "Die Template-Generierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    }
  };
  
  // Special case for concept messages that need confirmation
  if (requiresConfirmation && !isUser && !isConfirmed && onConfirmChanges && !message.isEmail) {
    return (
      <ConceptConfirmation 
        content={message.content}
        onConfirm={() => {
          if (onConfirmChanges) onConfirmChanges(message.id, message.content);
          setIsConfirmed(true);
        }}
        isLoading={isConfirming}
        error={generationError}
        onRetry={handleConfirmConcept}
        attempts={generationAttempts}
      />
    );
  }

  // Display template version banner
  if (message.templateId || extractedTemplate) {
    const previousVersions = templateVersioning.getPreviousVersions().map(v => ({
      id: v.id,
      createdAt: v.createdAt
    }));
    
    // Only display the template version banner if there's a templateId
    if (message.templateId) {
      return (
        <TemplateVersionBanner
          templateId={message.templateId}
          latestVersion={templateVersioning.getCurrentVersion() ? {
            id: templateVersioning.getCurrentVersion()?.id || "",
            createdAt: templateVersioning.getCurrentVersion()?.createdAt || ""
          } : undefined}
          previousVersions={previousVersions}
          onOpenVersion={(versionId) => {
            templateVersioning.setCurrentVersionId(versionId);
            const content = templateVersioning.getVersionContent(versionId);
            if (content && onEmailGenerated) {
              onEmailGenerated(content);
              window.dispatchEvent(new CustomEvent('show-template-preview'));
            }
          }}
          onEditTemplate={() => {
            if (message.templateId) {
              window.dispatchEvent(new CustomEvent('show-code-view'));
              
              // Get the template content using the hook
              const currentVersion = templateVersioning.getCurrentVersion();
              if (currentVersion?.content && onEmailGenerated) {
                onEmailGenerated(currentVersion.content);
              }
            } else if (extractedTemplate && onEmailGenerated) {
              window.dispatchEvent(new CustomEvent('show-code-view'));
              onEmailGenerated(extractedTemplate);
            }
          }}
        />
      );
    }
  }

  // Normal message display logic
  return (
    <div 
      className={`flex items-start ${isUser ? 'justify-end' : 'justify-start'} ${isWelcomeMessage ? 'welcome-message' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {!isUser && (
        <div className="flex-shrink-0 mr-3">
          <div className="h-8 w-8 rounded-full bg-jade flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
        </div>
      )}
      
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-[85%]`}
      >
        <div className={`px-4 py-3 rounded-lg ${isUser ? 'user-message' : 'bot-message'}`}>
          <div className="rich-text-content prose prose-sm dark:prose-invert">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                a: ({ node, ...props }) => (
                  <a 
                    {...props} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                  />
                ),
                code: ({ node, inline, className, children, ...props }) => {
                  if (inline) {
                    return (
                      <code className="px-1 py-0.5 rounded bg-gray-700 text-gray-200" {...props}>
                        {children}
                      </code>
                    );
                  }
                  return (
                    <div className="relative my-2">
                      <pre className="p-3 rounded bg-gray-900 text-gray-300 overflow-x-auto">
                        <code className={className} {...props}>
                          {children}
                        </code>
                      </pre>
                    </div>
                  );
                }
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
          
          {/* Action buttons for assistant messages */}
          {!isUser && message.content && !message.isEmail && !isWelcomeMessage && (
            <div className="flex flex-wrap gap-2 mt-3">
              {(message.metadata?.emailStage === 'concept' || message.content.includes('concept')) && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="bg-jade/10 text-xs border-jade/30 text-jade hover:bg-jade/20"
                  onClick={handleConfirmConcept}
                  disabled={isConfirming || isConfirmed}
                >
                  {isConfirming ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Generiere Template...
                    </>
                  ) : isConfirmed ? (
                    <>
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Template generiert
                    </>
                  ) : (
                    <>
                      <FileText className="h-3 w-3 mr-1" />
                      Template generieren
                    </>
                  )}
                </Button>
              )}
              
              {(!message.metadata?.emailStage || message.metadata?.emailStage === 'ideas') && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="bg-gray-800/80 text-xs border-gray-700 text-gray-300 hover:bg-gray-700/80"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onRequestIdeas && message.content) onRequestIdeas(message.content);
                    }}
                  >
                    <Lightbulb className="h-3 w-3 mr-1" />
                    Mehr Ideen
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="bg-gray-800/80 text-xs border-gray-700 text-gray-300 hover:bg-gray-700/80"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onOpenOfferForm) onOpenOfferForm();
                    }}
                  >
                    <FileText className="h-3 w-3 mr-1" />
                    Angebot erstellen
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="bg-gray-800/80 text-xs border-gray-700 text-gray-300 hover:bg-gray-700/80"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onOpenComparisonForm) onOpenComparisonForm();
                    }}
                  >
                    <Scale className="h-3 w-3 mr-1" />
                    Vergleich erstellen
                  </Button>
                </>
              )}
              
              {showPreviewButton && (
                <EmailTemplatePreviewButton onClick={() => {
                  if (extractedTemplate && onEmailGenerated) {
                    onEmailGenerated(extractedTemplate);
                    window.dispatchEvent(new CustomEvent('show-template-preview'));
                  } else if (message.templateId) {
                    // Get the template version and display it
                    const currentVersion = templateVersioning.getCurrentVersion();
                    if (currentVersion?.content && onEmailGenerated) {
                      onEmailGenerated(currentVersion.content);
                      window.dispatchEvent(new CustomEvent('show-template-preview'));
                    }
                  }
                }} />
              )}
            </div>
          )}
          
          {/* Show preview button for templates in message */}
          {(extractedTemplate || message.templateId) && (
            <div className="flex gap-2 mt-3">
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-jade/10 text-xs border-jade/30 text-jade hover:bg-jade/20"
                onClick={() => {
                  if (extractedTemplate && onEmailGenerated) {
                    onEmailGenerated(extractedTemplate);
                    window.dispatchEvent(new CustomEvent('show-template-preview'));
                  } else if (message.templateId) {
                    // Get the template version and display it
                    const currentVersion = templateVersioning.getCurrentVersion();
                    if (currentVersion?.content && onEmailGenerated) {
                      onEmailGenerated(currentVersion.content);
                      window.dispatchEvent(new CustomEvent('show-template-preview'));
                    }
                  }
                }}
              >
                <FileText className="h-3 w-3 mr-1" />
                Template ansehen
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-jade/10 text-xs border-jade/30 text-jade hover:bg-jade/20"
                onClick={() => {
                  if (message.templateId) {
                    window.dispatchEvent(new CustomEvent('show-code-view'));
                    
                    // Get the template content using the hook
                    const currentVersion = templateVersioning.getCurrentVersion();
                    if (currentVersion?.content && onEmailGenerated) {
                      onEmailGenerated(currentVersion.content);
                    }
                  } else if (extractedTemplate && onEmailGenerated) {
                    window.dispatchEvent(new CustomEvent('show-code-view'));
                    onEmailGenerated(extractedTemplate);
                  }
                }}
              >
                <Pen className="h-3 w-3 mr-1" />
                Bearbeiten
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ChatMessage;
